import {
  Grid,
  InputAdornment,
  FormControlLabel,
  FormLabel,
  FormControl,
  RadioGroup,
  Radio,
} from "@material-ui/core";
import React, { useEffect, useMemo, useState } from "react";
import { Qualification } from "../../domain/Qualification";
import { SectionProps } from "./SectionProps";
import { Controller, useWatch } from "react-hook-form";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { SearchField } from "@ofqual/portal-components/components/SearchField";
import { Select } from "@ofqual/portal-components/components/Select";
import { Checkbox } from "@ofqual/portal-components/components/Checkbox";
import { Error } from "../common/Error";
import { fieldLabels } from "./fieldLabels";
import {
  aoService,
  aoServiceSearch,
  apprenticeshipStandardsService,
  assessmentMethodsService,
  cachedLevelsService,
  gradingScalesService,
  overallGradingTypeService,
  qualificationTypeService,
  sectorSubjectService,
  entitlementFrameworkDesignationService,
} from "../../services";
import { QueryParams } from "@ofqual/portal-core/core/types";
import { UserType } from "../../redux/types";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { ItemType } from "@ofqual/portal-components/components/types";
import { toGroupedList } from "@ofqual/portal-core/core/utils/listHelpers";
import { FileInput } from "@ofqual/portal-components/components/FileInput";
import { AttachmentList } from "../common/AttachmentList";
import { PrivateQualificationDialog } from "./PrivateQualificationDialog";
import { UserRole } from "../../domain/UserRole";

export const QualificationDetails: React.FC<SectionProps<Qualification>> = ({
  control,
  errors,
  disabled,
  isEditForm,
  getValues,
  setValue,
  user: { userType, token, isInRole },
  lookupTracker,
  item,
  deleteAttachment,
  downloadAttachment,
  registerCustomError,
  formState: { isSubmitted },
}) => {
  const mappedOrgLookupService = useMemo(
    () => ({
      get: (id: string) => aoService.get(id.toString(), { token }),
      search: (params?: QueryParams) =>
        aoServiceSearch.search(params, { token }),
    }),
    [token]
  );

  const canAccessCceaFields =
    userType === UserType.CCEA || isInRole(UserRole.ITAdmin);

  const [
    sectorSubjectAreaLookups,
    levelsLookups,
    subLevelLookups,
    qualificationTypeLookups,
    overallGradingTypeLookups,
    gradingScaleLookups,
    assessmentMethodsLookups,
    entitlementFrameworkLookups,
  ] = useLookup(
    sectorSubjectService,
    {
      service: cachedLevelsService,
      id: "levelCode",
      label: "levelDescription",
    },
    {
      service: cachedLevelsService,
      id: "subLevelCode",
      label: "subLevelDescription",
    },
    {
      service: qualificationTypeService,
      id: "id",
      label: "description",
    },
    {
      service: overallGradingTypeService,
      id: "id",
      label: "description",
    },
    {
      service: gradingScalesService,
      id: "id",
      label: "description",
    },
    {
      service: assessmentMethodsService,
      id: "id",
      label: "description",
    },
    {
      service: entitlementFrameworkDesignationService,
      id: "id",
      label: "description",
    }
  );

  const mappedApprenticeshipStandardLookupService = useMemo(
    () => ({
      get: (id: string) =>
        apprenticeshipStandardsService.get(id.toString(), { token }),
      search: (params?: QueryParams) =>
        apprenticeshipStandardsService.search(params || {}, { token }),
    }),
    [token]
  );

  const groupedSectorSubjectAreas = toGroupedList(sectorSubjectAreaLookups, {
    id: "id",
    label: "label",
    group: "group",
  });

  const {
    qualLevel,
    qualSubLevel,
    qualificationType,
    offeredInNorthernIreland,
    offeredInEngland,
    overallGradingType,
  } = useWatch({
    control,
    name: [
      "qualLevel",
      "qualSubLevel",
      "qualificationType",
      "offeredInNorthernIreland",
      "offeredInEngland",
      "overallGradingType",
    ],
  });

  const EPA_QUAL_TYPE = 25;
  const AAQ_QUAL_TYPE = 36; // Apprenticeship Assessment Qualification (AAQ)

  const isEpaOrAaqQualType =
    !!qualificationType && (Number(qualificationType) === EPA_QUAL_TYPE || Number(qualificationType) === AAQ_QUAL_TYPE) ;

  const isCheckboxRequired =
    isSubmitted && !(offeredInEngland || offeredInNorthernIreland);

  useEffect(() => {
    registerCustomError("offeredInEngland", isCheckboxRequired);
  }, [registerCustomError, isCheckboxRequired]);

  useEffect(() => {
    const id = subLevelLookups.find((x) => x.levelCode === qualLevel && x.id === qualSubLevel)?.levelId;
    setValue(
      "qualLevelId",
      id
    );
  }, [qualLevel, qualSubLevel, subLevelLookups, setValue]);

  useEffect(() => {
    // grading type of 1 == Pass/Fail, therefore no applicable scale.
    if (overallGradingType === 1) {
      setDisableGradingScale(true);
      setValue("gradingScale", undefined);
    }
  }, [overallGradingType, setValue]);

  const [isPrivateDialogOpen, setIsPrivateDialogOpen] = useState(false);
  const [disableGradingScale, setDisableGradingScale] = useState(false);

  return (
    <>
      <Grid container spacing={3}>
        {userType === UserType.Ofqual ? (
          <Grid item xs={12}>
            <Controller
              control={control}
              rules={{ required: true }}
              name="organisationId"
              as={
                <SearchField
                  searchFieldVariant="single"
                  label="Owner organisation"
                  datatest="qual-ao"
                  service={mappedOrgLookupService}
                  disabled={disabled}
                  readOnly={isEditForm}
                  required
                  lookupTracker={lookupTracker}
                />
              }
            />
            {errors.organisationId && (
              <Error data-test="owner-org-validation">
                This field is required
              </Error>
            )}
          </Grid>
        ) : null}
        <Grid item xs={12}>
          <Controller
            control={control}
            name="organisationReferenceNumber"
            as={
              <TextField
                label={fieldLabels.organisationReferenceNumber}
                data-test="organisation-reference-number"
                fullWidth
                readOnly={disabled}
              />
            }
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={control}
            name="title"
            rules={{ required: true }}
            as={
              <TextField
                label={fieldLabels.title}
                data-test="qual-title"
                fullWidth
                readOnly={disabled}
                required
              />
            }
          />
          {errors.title?.type === "required" && (
            <Error data-test="title-validation">This field is required</Error>
          )}
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={control}
            name="linkToSpecification"
            rules={{
              required: true,
              validate: (value) =>
                value.toLowerCase().startsWith("http://") ||
                value.toLowerCase().startsWith("https://"),
            }}
            as={
              <TextField
                label={fieldLabels.linkToSpecification}
                data-test="qual-link"
                fullWidth
                readOnly={disabled}
                required
              />
            }
          />
          {errors.linkToSpecification?.type === "required" && (
            <Error data-test="link-to-spec-validation">
              This field is required
            </Error>
          )}
          {errors.linkToSpecification?.type === "validate" && (
            <Error>Must be a valid URL</Error>
          )}
        </Grid>
        <Grid item xs={6}>
          <Controller
            control={control}
            name="qualLevel"
            rules={{ required: true }}
            render={(props) => {
              const _onChange = (event: any) => {
                if (event.target.value !== "EntryLevel") {
                  setValue("qualSubLevel", "None");
                } else {
                  setValue("qualSubLevel", "Entry1");
                }
                props.onChange(event);
              };
              return (
                <Select
                  datatest="qual-level-picklist"
                  {...props}
                  label={fieldLabels.qualLevel}
                  readOnly={disabled}
                  lookupTracker={lookupTracker}
                  required
                  onChange={_onChange}
                >
                  {levelsLookups.filter(
                    (value, index, self) =>
                      self.findIndex((x) => x.id === value.id) === index
                  )}
                </Select>
              );
            }}
          />
          {errors.qualLevel?.type === "required" && (
            <Error data-test="qual-level-validation">
              This field is required
            </Error>
          )}
        </Grid>
        <Grid item xs={6}>
          <Controller
            control={control}
            name="qualSubLevel"
            rules={{ required: true }}
            as={
              <Select
                datatest="qual-sub-level-picklist"
                label={fieldLabels.qualSubLevel}
                disabled={!qualLevel}
                readOnly={disabled}
                lookupTracker={lookupTracker}
                required
              >
                {subLevelLookups.filter((x) => x.levelCode === qualLevel)}
              </Select>
            }
          />
          {errors.qualSubLevel?.type === "required" && (
            <Error>This field is required</Error>
          )}
        </Grid>
        <Grid item xs={12}>
          <Controller
            rules={{ required: true }}
            control={control}
            name="qualificationType"
            render={(props) => {
              const _onChange = (event: any) => {
                // reset epa field if changes
                setValue("epaStandardId", "");
                if (event.target.value === EPA_QUAL_TYPE) {
                  // if EPA reset below fields
                  setValue("qualGLH", "");
                  setValue("qualTQT", "");
                }
                props.onChange(event);
              };
              return (
                <Select
                  datatest="qual-type-picklist"
                  {...props}
                  label={fieldLabels.qualificationType}
                  readOnly={disabled}
                  lookupTracker={lookupTracker}
                  required
                  onChange={_onChange}
                >
                  {qualificationTypeLookups}
                </Select>
              );
            }}
          />
          {errors.qualificationType?.type === "required" && (
            <Error>This field is required</Error>
          )}
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={control}
            name="epaStandardId"
            rules={{ required: isEpaOrAaqQualType }}
            render={({ onChange, ...props }) => (
              <SearchField
                label={fieldLabels.epaStandardId}
                disabled={!isEpaOrAaqQualType}
                datatest="epa-standard-picklist"
                searchFieldVariant="single"
                readOnly={disabled}
                service={mappedApprenticeshipStandardLookupService}
                required
                onChange={(e) => {
                  onChange(e);
                }}
                {...props}
              ></SearchField>
            )}
          />
          {/* <Controller
            control={control}
            name="epaStandardId"
            rules={{ required: isEpaOrAaqQualType }}
            as={
              <Select
                datatest="epa-standard-picklist"
                label={fieldLabels.epaStandardId}
                disabled={!isEpaOrAaqQualType}
                readOnly={disabled}
                lookupTracker={lookupTracker}
                required={isEpaOrAaqQualType}
              >
                {apprentinceshipStandardsLookups}
              </Select>
            }
          /> */}
          {errors.epaStandardId?.type === "required" && (
            <Error>This field is required</Error>
          )}
        </Grid>
        <Grid item xs={6}>
          <Controller
            control={control}
            name="qualGLH"
            rules={{ required: !isEpaOrAaqQualType }}
            as={
              <TextField
                label={fieldLabels.qualGLH}
                data-test="qual-glh"
                fullWidth
                readOnly={disabled}
                disabled={isEpaOrAaqQualType}
                required={!isEpaOrAaqQualType}
                type="number"
                inputProps={{ min: 0 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">hours</InputAdornment>
                  ),
                }}
              />
            }
          />
          {errors.qualGLH?.type === "required" && (
            <Error data-test="glh-validation">This field is required</Error>
          )}
        </Grid>
        <Grid item xs={6}>
          <Controller
            control={control}
            name="qualTQT"
            rules={{ required: !isEpaOrAaqQualType }}
            as={
              <TextField
                label={fieldLabels.qualTQT}
                data-test="qual-tqt"
                fullWidth
                readOnly={disabled}
                disabled={isEpaOrAaqQualType}
                required={!isEpaOrAaqQualType}
                type="number"
                inputProps={{ min: 0 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">hours</InputAdornment>
                  ),
                }}
              />
            }
          />
          {errors.qualTQT?.type === "required" && (
            <Error data-test="tqt-validation">This field is required</Error>
          )}
        </Grid>
        <Grid item xs={6}>
          <Controller
            control={control}
            name="qualCredit"
            as={
              <TextField
                variant="outlined"
                label={fieldLabels.qualCredit}
                data-test="qual-credit-value"
                fullWidth
                readOnly={disabled}
                type="number"
                inputProps={{ min: 0 }}
              />
            }
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            control={control}
            name="qualSectorSubjectArea"
            rules={{ required: true }}
            as={
              <Select
                datatest="qual-ssa-picklist"
                label={fieldLabels.qualSectorSubjectArea}
                readOnly={disabled}
                lookupTracker={lookupTracker}
                required
              >
                {groupedSectorSubjectAreas as ItemType[]}
              </Select>
            }
          />
          {errors.qualSectorSubjectArea?.type === "required" && (
            <Error data-test="ssa-validation">This field is required</Error>
          )}
        </Grid>
        <Grid item xs={6}>
          <Controller
            control={control}
            name="overallGradingType"
            rules={{ required: true }}
            render={(props) => {
              const _onChange = (event: any) => {
                // set grading scale to None if Pass/Fail
                if (event.target.value === 1) {
                  setValue("gradingScale", undefined);
                  setDisableGradingScale(true);
                } else {
                  setDisableGradingScale(false);
                }
                props.onChange(event);
              };
              return (
                <Select
                  datatest="qual-grading-type-picklist"
                  label={fieldLabels.overallGradingType}
                  readOnly={disabled}
                  lookupTracker={lookupTracker}
                  required
                  onChange={_onChange}
                  value={props.value}
                >
                  {overallGradingTypeLookups}
                </Select>
              );
            }}
          />
          {errors.overallGradingType?.type === "required" && (
            <Error data-test="overall-grading-type-validation">
              This field is required
            </Error>
          )}
        </Grid>
        <Grid item xs={6}>
          <Controller
            control={control}
            name="gradingScale"
            rules={{ required: !disableGradingScale }}
            as={
              <Select
                datatest="qual-grading-scale-picklist"
                label={fieldLabels.gradingScale}
                readOnly={disabled}
                lookupTracker={lookupTracker}
                required={!disableGradingScale}
                disabled={disableGradingScale}
              >
                {gradingScaleLookups}
              </Select>
            }
          />
          {errors.gradingScale?.type === "required" && (
            <Error data-test="grading-scale-validation">
              This field is required
            </Error>
          )}
        </Grid>
        <Grid item xs={12}>
          <Controller
            required
            control={control}
            rules={{
              validate: (value) => !!value.length,
            }}
            name="qualAssessmentMethods"
            as={
              <Select
                selectVariant="multiple"
                label={fieldLabels.qualAssessmentMethods}
                data-test="assessment-methods"
                readOnly={disabled}
                required
                lookupTracker={lookupTracker}
              >
                {assessmentMethodsLookups}
              </Select>
            }
          />
          {errors.qualAssessmentMethods && (
            <Error data-test="assessment-methods-validation">
              This field is required
            </Error>
          )}
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={control}
            name="specialism"
            as={
              <TextField
                label={fieldLabels.specialism}
                data-test="specialism"
                fullWidth
                readOnly={disabled}
                multiline
                rows={5}
              />
            }
          />
        </Grid>

        <Grid item xs={12}>
          <FormLabel component="legend" required>
            Offered in
          </FormLabel>
          <Controller
            control={control}
            name="offeredInEngland"
            render={(props) => (
              <FormControlLabel
                control={
                  <Checkbox
                    data-test="offered-in-england-checkbox"
                    onChange={(e: any) => {
                      props.onChange(e.target.checked);
                    }}
                    checked={props.value}
                    disabled={disabled}
                  />
                }
                label="England"
              />
            )}
          />
          <Controller
            control={control}
            name="offeredInNorthernIreland"
            render={(props) => (
              <FormControlLabel
                control={
                  <Checkbox
                    data-test="offered-in-ni-checkbox"
                    onChange={(e: any) => {
                      if (!e.target.checked) {
                        setValue("approvedForDELFundedProgramme", null);
                        setValue("entitlementFrameworkDesignation", null);
                        setValue("niDiscountCode", null);
                        setValue("gcseSizeEquivalence", null);
                        setValue("gceSizeEquivalence", null);
                      }
                      props.onChange(e.target.checked);
                    }}
                    checked={props.value}
                    disabled={disabled}
                  />
                }
                label="Northern Ireland"
              />
            )}
          />
          {isCheckboxRequired && (
            <>
              <br />
              <Error data-test="offered-in-validation">
                Please select at least one option
              </Error>
            </>
          )}
        </Grid>
        {offeredInNorthernIreland ? (
          <>
            {canAccessCceaFields && (
              <>
                <Grid item xs={12}>
                  <FormLabel component="legend">
                    {fieldLabels.approvedForDELFundedProgramme}
                  </FormLabel>
                  <Controller
                    control={control}
                    name="approvedForDELFundedProgramme"
                    render={(props) => (
                      <Checkbox
                        onChange={(e: any) => {
                          props.onChange(e.target.checked);
                        }}
                        data-test="approved-delf-checkbox"
                        checked={props.value}
                        disabled={disabled}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="entitlementFrameworkDesignation"
                    as={
                      <Select
                        label={fieldLabels.entitlementFrameworkDesignation}
                        data-test="qualifications-entitlement-methods"
                        disabled={disabled}
                        lookupTracker={lookupTracker}
                      >
                        {entitlementFrameworkLookups}
                      </Select>
                    }
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <Controller
                control={control}
                name="niDiscountCode"
                as={
                  <TextField
                    label={fieldLabels.niDiscountCode}
                    data-test="ni-discount-code"
                    fullWidth
                    readOnly={disabled}
                  />
                }
              />
            </Grid>
            {canAccessCceaFields && (
              <>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="gcseSizeEquivalence"
                    as={
                      <TextField
                        label={fieldLabels.gcseSizeEquivalence}
                        data-test="gcse-size-equivalence"
                        fullWidth
                        readOnly={disabled}
                      />
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="gceSizeEquivalence"
                    as={
                      <TextField
                        label={fieldLabels.gceSizeEquivalence}
                        data-test="gce-size-equivalence"
                        fullWidth
                        readOnly={disabled}
                      />
                    }
                  />
                </Grid>
              </>
            )}
          </>
        ) : null}
        <Grid item xs={12}>
          <FormLabel component="legend">
            Qualification Appropriate For Age Ranges
          </FormLabel>
          <Controller
            control={control}
            name="preSixteen"
            render={(props) => (
              <FormControlLabel
                control={
                  <Checkbox
                    data-test="pre-16-checkbox"
                    onChange={(e: any) => {
                      props.onChange(e.target.checked);
                    }}
                    checked={props.value}
                    disabled={disabled}
                  />
                }
                label="Pre 16"
              />
            )}
          />
          <Controller
            control={control}
            name="sixteenToEighteen"
            render={(props) => (
              <FormControlLabel
                control={
                  <Checkbox
                    data-test="16-to-18-checkbox"
                    onChange={(e: any) => {
                      props.onChange(e.target.checked);
                    }}
                    checked={props.value}
                    disabled={disabled}
                  />
                }
                label="16-18"
              />
            )}
          />
          <Controller
            control={control}
            name="eighteenPlus"
            render={(props) => (
              <FormControlLabel
                control={
                  <Checkbox
                    data-test="18-plus-checkbox"
                    onChange={(e: any) => {
                      props.onChange(e.target.checked);
                    }}
                    checked={props.value}
                    disabled={disabled}
                  />
                }
                label="18+"
              />
            )}
          />
          <Controller
            control={control}
            name="nineteenPlus"
            render={(props) => (
              <FormControlLabel
                control={
                  <Checkbox
                    data-test="19-plus-checkbox"
                    onChange={(e: any) => {
                      props.onChange(e.target.checked);
                    }}
                    checked={props.value}
                    disabled={disabled}
                  />
                }
                label="19+"
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <FormLabel component="legend">
            Intention to seeking funding in
          </FormLabel>
          <Controller
            control={control}
            name="fundingInEngland"
            render={(props) => (
              <FormControlLabel
                control={
                  <Checkbox
                    data-test="england-funding-checkbox"
                    onChange={(e: any) => {
                      props.onChange(e.target.checked);
                    }}
                    checked={props.value}
                    disabled={disabled}
                  />
                }
                label="England"
              />
            )}
          />
          <Controller
            control={control}
            name="fundingInNorthernIreland"
            render={(props) => (
              <FormControlLabel
                control={
                  <Checkbox
                    data-test="ni-funding-checkbox"
                    onChange={(e: any) => {
                      props.onChange(e.target.checked);
                    }}
                    checked={props.value}
                    disabled={disabled}
                  />
                }
                label="Northern Ireland"
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <FormLabel component="legend">
            {fieldLabels.isThisQualificationPrivate}
          </FormLabel>
          <Controller
            control={control}
            name="isThisQualificationPrivate"
            render={(props) => (
              <Checkbox
                data-test="is-qual-private-checkbox"
                onChange={(e: any) => {
                  props.onChange(e.target.checked);
                  if (e.target.checked) {
                    setIsPrivateDialogOpen(true);
                  }
                }}
                checked={props.value}
                disabled={disabled}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={control}
            name="optionalRoutesToCompleteQual"
            as={
              <TextField
                label={fieldLabels.optionalRoutesToCompleteQual}
                data-test="optional-routes-to-complete-qual"
                fullWidth
                readOnly={disabled}
                multiline
                rows={5}
              />
            }
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={control}
            name="offeredInternationally"
            rules={{ validate: (value) => value !== null }}
            render={(props) => (
              <FormControl component="fieldset" required disabled={disabled}>
                <FormLabel id="offeredInternationally-label" component="legend">
                  {fieldLabels.offeredInternationally}
                </FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="offeredInternationally-label"
                  name="offeredInternationally"
                  data-test="radiogroup-offeredInternationally"
                  onChange={(e) => props.onChange(e.target.value === "true")}
                >
                  <FormControlLabel
                    value={true}
                    label="Yes"
                    control={<Radio />}
                    checked={props.value === true}
                  />
                  <FormControlLabel
                    value={false}
                    label="No"
                    control={<Radio />}
                    checked={props.value === false}
                  />
                </RadioGroup>
              </FormControl>
            )}
          />
          {errors.offeredInternationally && (
            <>
              <br></br>
              <Error data-test="currently-offering-internationally-validation">
                You must select at least one option
              </Error>
            </>
          )}
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={control}
            name="files"
            defaultValue={[]}
            as={
              <FileInput
                multiple={true}
                dataTest="select-files-button"
                disabled={disabled}
              />
            }
          />
          {item?.qualAttachments ? (
            <AttachmentList
              canEdit={!disabled}
              attachments={item.qualAttachments}
              deleteAttachment={deleteAttachment}
              getBlob={downloadAttachment!}
            />
          ) : null}
        </Grid>
      </Grid>
      <PrivateQualificationDialog
        setValue={setValue}
        isDialogOpen={isPrivateDialogOpen}
        onClose={() => setIsPrivateDialogOpen(false)}
      />
    </>
  );
};
